import { createFileRoute } from '@tanstack/react-router'
import { CalculatorIcon, LightbulbIcon, StoreIcon, TagIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useVendorPrices } from '@/api/vendor-prices'
import type { VendorPriceRow } from '@/api/vendor-prices'
import { useWorkspace } from '@/api/workspace'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { VendorPriceDialog } from '@/components/vendor-prices/vendor-price-dialog'

// Vendor Prices (UI-SCREENS S-04, 06a #184): one row per Variant with the price in effect today
// — the default and the override at every channel that has one — searchable by SKU or name,
// phone-first. Owner only; the API answers Staff with 403 and the sidebar hides the link.
//
// Each row also shows the Variant's COGS with its estimated flag and the Suggested Vendor Price
// (08d #205), which prefills the set-price modal for the default price (#205 Q3) and writes nothing.
export const Route = createFileRoute('/w/$slug/vendor-prices/')({
  component: VendorPricesScreen,
  staticData: { breadcrumb: 'vendorPrices.title' },
})

function VendorPricesScreen() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const workspace = useWorkspace(slug)
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [openFor, setOpenFor] = useState<number | null>(null)
  const prices = useVendorPrices(slug, { q, page })
  const rows = prices.data?.items ?? []

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-semibold">{t('vendorPrices.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('vendorPrices.subtitle')}</p>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="vendor-prices-search">{t('vendorPrices.searchLabel')}</Label>
        <Input
          id="vendor-prices-search"
          className="h-11"
          autoComplete="off"
          placeholder={t('vendorPrices.searchPlaceholder')}
          value={q}
          onChange={(event) => {
            setQ(event.target.value)
            setPage(1)
          }}
        />
      </div>

      {prices.isPending ? (
        <LoadingSkeleton />
      ) : prices.isError || !prices.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : rows.length === 0 ? (
        <RecordListEmpty>{t('vendorPrices.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {rows.map((row) => (
            <VariantPrices
              key={row.variant.id}
              slug={slug}
              row={row}
              adoptedOn={workspace.data?.adopted_on ?? null}
              open={openFor === row.variant.id}
              onOpenChange={(open) => setOpenFor(open ? row.variant.id : null)}
            />
          ))}
        </RecordList>
      )}

      <div className="flex gap-2">
        {page > 1 ? (
          <Button variant="outline" className="h-11" onClick={() => setPage(page - 1)}>
            {t('vendorPrices.previousPage')}
          </Button>
        ) : null}
        {prices.data?.next_page ? (
          <Button variant="outline" className="h-11" onClick={() => setPage(page + 1)}>
            {t('vendorPrices.nextPage')}
          </Button>
        ) : null}
      </div>
    </section>
  )
}

function VariantPrices({
  slug,
  row,
  adoptedOn,
  open,
  onOpenChange,
}: {
  slug: string
  row: VendorPriceRow
  adoptedOn: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()

  // One Variant per row (no detail page): the figure is the default price with its effective
  // date, the COGS and Suggested Vendor Price sit in the meta line, and the channel overrides are a
  // compact nested list. Set price opens the modal.
  return (
    <RecordListItem
      title={`${row.variant.product.name} · ${row.variant.name}`}
      badges={
        <>
          {row.cogs?.estimated ? <Badge variant="secondary">{t('cogs.estimated')}</Badge> : null}
          {row.variant.archived ? <Badge variant="outline">{t('vendorPrices.archived')}</Badge> : null}
        </>
      }
      meta={[
        { icon: TagIcon, label: t('variants.form.sku'), value: row.variant.sku },
        {
          icon: CalculatorIcon,
          label: t('vendorPrices.cogs'),
          value: `${t('vendorPrices.cogs')}: ${row.cogs ? `Rp ${formatMoney(row.cogs.total)}` : '—'}`,
        },
        {
          icon: LightbulbIcon,
          label: t('cogs.suggestedPrice'),
          value: `${t('cogs.suggestedPrice')}: ${row.suggested_vendor_price === null ? '—' : `Rp ${formatMoney(row.suggested_vendor_price)}`}`,
        },
      ]}
      description={
        !row.cogs ? t('cogs.suggestedNoCogs') : row.suggested_vendor_price === null ? t('cogs.suggestedNoMargin') : undefined
      }
      figure={row.default_price ? `Rp ${formatMoney(row.default_price.price)}` : '—'}
      caption={
        row.default_price
          ? t('vendorPrices.since', { date: formatBusinessDate(row.default_price.effective_from) })
          : t('vendorPrices.noPrice')
      }
      actions={
        <>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(true)}>
            {t('vendorPrices.setPrice')}
          </Button>
          <VendorPriceDialog slug={slug} row={row} adoptedOn={adoptedOn} open={open} onOpenChange={onOpenChange} />
        </>
      }
    >
      {row.overrides.length > 0 ? (
        <ul className="flex flex-col gap-0.5 text-muted-foreground" aria-label={t('vendorPrices.overrides')}>
          {row.overrides.map((override) => (
            <li key={override.id} className="flex flex-wrap items-center gap-x-1.5">
              <StoreIcon aria-hidden="true" className="size-3.5 shrink-0" />
              <span>{override.sales_channel.name}</span>
              {override.sales_channel.archived ? <Badge variant="outline">{t('vendorPrices.archived')}</Badge> : null}
              <span className="font-medium text-foreground tabular-nums">Rp {formatMoney(override.price)}</span>
              <span>{t('vendorPrices.since', { date: formatBusinessDate(override.effective_from) })}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </RecordListItem>
  )
}
